import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import * as leaflet from 'leaflet';
import toGeoJson from '@mapbox/togeojson';

import { PlotAttributes, TripAttributes } from '~app/models';
import { TripService } from '~app/services';
import { getLettersForIndex } from '~app/services/trip.service';

class MapLayerData {
  id: number;
  name: string;
  featurelayer: any;
  allpoints: any;
  enabled: boolean = true;
}

class MapFileData {
  id: number;
  maplayers: MapLayerData[];
  filename: string;
  enabled: boolean = true;
}

const mapPinIcon = leaflet.icon({
  iconUrl: 'assets/img/icons/map-markers/gray-pin-for-letters.png',
  iconSize: [30, 30], // size of the icon
  iconAnchor: [15, 30], // point of the icon which will correspond to marker's location
  popupAnchor: [0, -30], // point from which the popup should open relative to the iconAnchor,
  tooltipAnchor: [0, -20],
});
const mapPinTooltipOptions: leaflet.TooltipOptions = {
  permanent: true,
  direction: 'center',
  className: 'map-marker-letter',
} as const;

@Component({
  imports: [CommonModule],
  selector: 'ciao-trip-map',
  templateUrl: './trip-map.component.html',
  styleUrls: ['./trip-map.component.less'],
})
export class TripMapComponent implements AfterViewInit, OnChanges {
  constructor(
    private readonly tripService: TripService,
    private readonly http: HttpClient
  ) {}

  @Input({ required: true }) trip: TripAttributes;
  /** Reflects the parent's expanded state so the in-map toggle shows the right icon. */
  @Input() expanded: boolean = false;
  @Output() readonly addLocation = new EventEmitter<PlotAttributes['coords']>();
  /** Emitted when the expand/collapse button in the zoom control is clicked. */
  @Output() readonly expandToggle = new EventEmitter<void>();
  map: leaflet.Map;
  /** The expand/collapse control injected between the zoom in/out buttons. */
  private expandButton?: HTMLElement;
  /** save the locations as a separate collection so we can show points on the map before they're officially added. */
  locations: PlotAttributes['waypoint'][] = [];
  /** The locations / map pins and lines between as features on the map */
  mapfeatures: any[];
  addPointLocationsEnabled: boolean = false;
  showShortHelp: boolean = false;
  showLongHelp: boolean = false;

  get addWaypointIconSrc() {
    let active = this.addPointLocationsEnabled ? '-active' : '';

    return `assets/img/icons/map-markers/add-waypoint-icon${active}.svg`;
  }

  get smallAddsrc() {
    let active = this.addPointLocationsEnabled ? '-active' : '';
    return `assets/img/icons/map-markers/mobile-waypoint-icon${active}.svg`;
  }

  ngOnInit(): void {}

  /** Update the in-map expand button's icon/labels to match the expanded state. */
  private updateExpandIcon() {
    if (!this.expandButton) return;
    const label = this.expanded ? 'Collapse map' : 'Expand map';
    this.expandButton.title = label;
    this.expandButton.setAttribute('aria-label', label);
    this.expandButton.innerHTML = `<span class="material-icons" aria-hidden="true" style="font-size:16px">${
      this.expanded ? 'close_fullscreen' : 'open_in_full'
    }</span>`;
  }

  /** Recompute Leaflet's dimensions after the container is resized (e.g. when
   * the map is expanded/collapsed) so tiles re-render and stay centered. */
  public refreshSize() {
    if (!this.map) return;
    this.map.invalidateSize();
    this.reframeMap();
  }

  ngAfterViewInit() {
    this.loadBaseMap();
    // The responsive layout finalizes the container's height after this tick;
    // remeasure so Leaflet matches its box rather than rendering the view at a
    // stale (larger) size that spills past the card on smaller screens.
    setTimeout(() => this.refreshSize());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trip && this.trip) {
      this.reloadPointsFromTrip(this.trip);
      this.reframeMap();
    }
    if (changes.expanded) {
      this.updateExpandIcon();
    }
  }

  blurOnEscape($event: KeyboardEvent) {
    if ($event.key === 'Escape') {
      ($event.target as HTMLDivElement).blur();
    }
  }

  /** Load the base layers of the map.  This should only be called once. */
  private loadBaseMap() {
    console.log('loadBaseMap');
    this.map = leaflet.map('map');
    this.map.attributionControl.setPosition('bottomleft');
    this.map.zoomControl.setPosition('bottomright');
    this.map.scrollWheelZoom.disable();

    let zoomContainer = this.map.zoomControl.getContainer();
    zoomContainer
      .querySelector('.leaflet-control-zoom-in')
      .classList.add('mat-elevation-z4', '.mat-mdc-elevation-specific');
    zoomContainer
      .querySelector('.leaflet-control-zoom-out')
      .classList.add('mat-elevation-z4', '.mat-mdc-elevation-specific');

    // Inject an expand/collapse button between the zoom in and zoom out buttons.
    const expandButton = leaflet.DomUtil.create(
      'a',
      'leaflet-control-zoom-expand mat-elevation-z4'
    ) as HTMLAnchorElement;
    expandButton.href = '#';
    expandButton.setAttribute('role', 'button');
    expandButton.style.display = 'flex';
    expandButton.style.alignItems = 'center';
    expandButton.style.justifyContent = 'center';
    this.expandButton = expandButton;
    this.updateExpandIcon();

    const zoomOut = zoomContainer.querySelector('.leaflet-control-zoom-out');
    zoomContainer.insertBefore(expandButton, zoomOut);

    leaflet.DomEvent.disableClickPropagation(expandButton);
    leaflet.DomEvent.on(expandButton, 'click', (e: Event) => {
      leaflet.DomEvent.preventDefault(e);
      leaflet.DomEvent.stopPropagation(e);
      this.expandToggle.emit();
    });

    leaflet
      .tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      })
      .addTo(this.map);

    let self = this;
    this.map.on('click', function (e) {
      var coord = e.latlng;
      self.addNewPointLocationToData(coord);
    });

    this.reframeMap();
  }

  /** turn the trip locations data into "point locations", filter by type, sort, augment. */
  private reloadPointsFromTrip(trip: TripAttributes): void {
    // find all the locations on the map that actually seem to represent a point.
    this.locations = trip.locations
      .map((location) => location.waypoint)
      .filter((waypoint) => !!waypoint);
    this.reloadTopMapLayers();
  }

  /** reload just the added points on the map */
  private reloadTopMapLayers() {
    this.clearMapFeatures();
    this.locations?.forEach((location) => {
      this.addPointLocationToMap(location);
    });
    this.addLineForPathPoints(this.locations);
  }

  public toggleAllowingAddingLocations() {
    this.setAllowingAddingLocations(!this.addPointLocationsEnabled);
  }

  private setAllowingAddingLocations(state: boolean) {
    this.addPointLocationsEnabled = state;
    if (state) {
      this.map.scrollWheelZoom.enable();
    } else {
      this.map.scrollWheelZoom.disable();
    }
  }

  /**
   * Add a new point to the map when you click.
   * Put the lat/long in the pinned row (event emitter)
   * Show the point on the map.
   * Don't edit the trip just yet.
   */
  private addNewPointLocationToData(coord: leaflet.LatLng) {
    console.log('You clicked the map at coord: ', coord);
    if (!this.addPointLocationsEnabled) {
      console.log('adding points is not enabled, tap the button');
      return;
    }

    // Make last location added temporary if trying to add multiple locations.
    // Todo: This will eventually change if/when we add a placeholder waypoint name and save automatically
    if (!this.locations[this.locations.length - 1]?.locationId) {
      this.locations.pop();
    }

    // Visually add the location to the map without really adding it.
    this.locations.push({
      locationId: '',
      letter: getLettersForIndex(this.trip.locations.length),
      latlon: coord,
    });

    this.reloadTopMapLayers();

    this.setAllowingAddingLocations(false);
    // Emit the new location so that the parent component can
    //   send it over to the pinned row of the locations grid.
    this.addLocation.emit({ lat: coord.lat, lon: coord.lng });
  }

  /**
   * called to add one marker on the map, this doesn't add another point location to the data, just called when the map is being rendered
   */
  private addPointLocationToMap(location: PlotAttributes['waypoint']): void {
    let marker = leaflet
      .marker(location.latlon, { icon: mapPinIcon })
      .addTo(this.map);
    marker.bindTooltip(location.letter, mapPinTooltipOptions);
    let { lat, lng } = location.latlon;

    // point popup
    // TODO: sanitize inputs

    // here we shorten the location.descrition to 19 characters, and if it is more than that, we add "..." to the end of it. this is for the markup below
    let shortenedDescription = location.description
      ? location.description.slice(0, 19)
      : '';
    if (location.description && location.description.length > 19) {
      shortenedDescription += '...';
    }

    let markup = `
    <span style="font-size:20px; font-weight:bold; padding-right:20px;">${location.description ? shortenedDescription : 'Location ' + location.letter}</span>
    <hr>
    <p style="margin: 0; font-size: 14px;"><strong>Lat:</strong>&nbsp;${lat}<br></p>
    <p style="margin: 0; font-size: 14px;"><strong>Long:</strong>&nbsp;${lng}<br></p>
   `;
    // markup += `<input type="button" value="I'm Okay" `;
    // markup += ` style="background-color: #7C4D38; color: white; border: 0px; border-radius: 10px; padding:10px; margin-left:100px; font-weight: bold;"`;
    // markup += ` onclick="document.querySelector('.imOkBridgeHref-${location.id}').click();" >`;
    marker.bindPopup(markup);

    if (this.mapfeatures == null) {
      this.mapfeatures = [];
    }
    this.mapfeatures.push(marker);
  }

  /**
   * leaving this hook here, when they click on the "im ok" button in a marker hover-over, this function ultimately gets called.
   * replace this code with something to actually check in "im ok" at the given location.
   */
  logImOk(event, location: any): void {
    console.log('im ok: ', location);
    alert('im ok at point ' + location.description);
  }

  /** displaying a line on the map between all the point locations */
  private addLineForPathPoints(locations: PlotAttributes['waypoint'][]): void {
    if (!locations) {
      return;
    }

    let array = locations.map((location) => location.latlon);
    let polyline = leaflet.polyline(array, {
      color: 'blue',
      weight: 3,
      opacity: 0.5,
      smoothFactor: 1,
    });
    polyline.addTo(this.map);

    if (this.mapfeatures == null) {
      this.mapfeatures = [];
    }
    this.mapfeatures.push(polyline);
  }

  /** just delete everything from the map */
  private clearMapFeatures(): void {
    if (!this.mapfeatures) {
      return;
    }
    for (let feature of this.mapfeatures) {
      this.map.removeLayer(feature);
    }
  }

  /** find all the points that are visible on the map and re-center the map on them with a little buffer */
  private reframeMap() {
    // find all points from all visible features on the map, recalculate extents, zoom/flyto new optimal extents/zoomlevel
    let allvisiblepoints = this.locations.map((location) => location.latlon);
    if (allvisiblepoints.length > 0) {
      let bounds = new leaflet.LatLngBounds(allvisiblepoints);
      this.map.fitBounds(bounds);
    } else {
      this.reframeMapNoData();
    }
  }

  /** What do we do when there are no points to frame the map around */
  private async reframeMapNoData() {
    // default center of contiguous united states
    let lat = 39.0,
      long = -98.0;
    let defaultzoom = 4;

    // try to get a little closer to users location with cached ip lookup from backend
    const apiUrl = `${this.tripService.apiUrl}/geolocation/my-location`;
    
    try {
      const response = await firstValueFrom(this.http
        .get<{ success: boolean; ip?: string; location: { lat: number; lon: number } }>(
          apiUrl
        ));

      if (response?.success && response.location) {
        lat = response.location.lat;
        long = response.location.lon;
        defaultzoom = 7;
      }
    } catch (exception) {
      // guess not. either they're on the VPN and this wouldnt be useful anyways, or some other general issue went wrong.
    }
    
    this.map.setView([lat, long], defaultzoom);
  }

  toggleShortHelp() {
    this.showShortHelp = !this.showShortHelp;
  }
  toggleLongHelp() {
    this.showLongHelp = !this.showLongHelp;
    // if we are closing the long help, close the short help too
    if (!this.showLongHelp) {
      this.showShortHelp = false;
    }
  }
}
